import { Icon } from "@iconify/react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

// Hide scrollbar CSS
const hideScrollbarStyle = {
  scrollbarWidth: "none" as const,
  msOverflowStyle: "none" as const,
};

const Color = {
  videoRed: "#FF0000",
  themeBlue: "#4ea4f5",
  textThemeDark: "#226fb7",
  textPrimary: "#000",
  textHintStrong: "#888",
  borderLight: "#e0e0e0",
  white: "#fff",
  bgLight: "#f8f9fa",
};

interface FeatureSectionYouTubeProps {
  showDescription?: boolean;
}

const FeatureSectionYouTube: React.FC<FeatureSectionYouTubeProps> = ({
  showDescription = true,
}) => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language?.startsWith("zh");
  const [activeTab, setActiveTab] = useState<"summary" | "chat">("summary");
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [currentPartIndex, setCurrentPartIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState("00:00");
  const [progressPercent, setProgressPercent] = useState(0);
  const [clickedSection, setClickedSection] = useState<number | null>(null);
  const [clickedCitation, setClickedCitation] = useState<string | null>(null);
  const [bulgingElement, setBulgingElement] = useState<string | null>(null); // Bulge preview before click
  const [isAnimationPaused, setIsAnimationPaused] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Video paused state for end of animation
  const [showBulletPoints, setShowBulletPoints] = useState(false); // Hide description bullet points until animation completes
  const [mousePosition, setMousePosition] = useState<{
    x: number;
    y: number;
    visible: boolean;
    clicking: boolean;
  }>({ x: 0, y: 0, visible: false, clicking: false });
  const transcriptScrollRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const resumeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const animationAbortRef = useRef<boolean>(false);
  const animationStartedRef = useRef<boolean>(false);
  const bulletPointsShownRef = useRef<boolean>(false); // Track if bullet points have ever been shown

  // Refs for dynamic click positioning
  const firstWordRef = useRef<HTMLSpanElement>(null); // First word in first paragraph
  const citation45Ref = useRef<HTMLSpanElement>(null); // 00:45 citation in Key Points

  // Only animate when 90% of card is visible (Google's standard)
  const isInView = useInView(cardRef, { amount: 0.9 });

  // Total video duration: 8:45 = 525 seconds
  const totalDuration = 525;

  // Tabs matching PDF style (Summary + AI Chat on right panel)
  const tabs = [
    {
      id: "summary" as const,
      label: t("landing.features.youtube.tabs.summary"),
    },
    { id: "chat" as const, label: t("landing.features.youtube.tabs.aiChat") },
  ];

  // Transcript content - recording style (timestamp + text)
  // Each part has both English words and Chinese words for localization
  const transcriptContent = [
    {
      timestamp: "00:00",
      seconds: 0,
      textKey: "modern.featureSections.youtube.mockContent.part0",
      words: [
        "Welcome",
        "to",
        "YouWo AI's",
        "YouTube",
        "learning",
        "feature.",
        "Today",
        "we'll",
        "show",
        "you",
        "how",
        "to",
        "turn",
        "any",
        "video",
        "into",
        "an",
        "interactive",
        "study",
        "session.",
      ],
      wordsZh: [
        "欢迎",
        "来到",
        "由我AI的",
        "YouTube",
        "学习",
        "功能。",
        "今天",
        "我们",
        "将",
        "展示",
        "如何",
        "将",
        "任何",
        "视频",
        "转变为",
        "互动",
        "学习",
        "课程。",
      ],
    },
    {
      timestamp: "00:15",
      seconds: 15,
      textKey: "modern.featureSections.youtube.mockContent.part1",
      words: [
        "Simply",
        "paste",
        "a",
        "YouTube",
        "link",
        "and",
        "our",
        "AI",
        "will",
        "automatically",
        "transcribe",
        "the",
        "entire",
        "video",
        "with",
        "accurate",
        "timestamps.",
      ],
      wordsZh: [
        "只需",
        "粘贴",
        "一个",
        "YouTube",
        "链接，",
        "我们的",
        "AI",
        "就会",
        "自动",
        "转录",
        "整个",
        "视频，",
        "并",
        "附带",
        "精确的",
        "时间戳。",
      ],
    },
    {
      timestamp: "00:30",
      seconds: 30,
      textKey: "modern.featureSections.youtube.mockContent.part2",
      words: [
        "You",
        "can",
        "click",
        "any",
        "timestamp",
        "to",
        "jump",
        "directly",
        "to",
        "that",
        "moment",
        "in",
        "the",
        "video.",
        "Perfect",
        "for",
        "reviewing",
        "specific",
        "sections.",
      ],
      wordsZh: [
        "你",
        "可以",
        "点击",
        "任何",
        "时间戳",
        "直接",
        "跳转",
        "到",
        "视频中的",
        "那个",
        "时刻。",
        "非常",
        "适合",
        "复习",
        "特定的",
        "章节。",
      ],
    },
    {
      timestamp: "00:45",
      seconds: 45,
      textKey: "modern.featureSections.youtube.mockContent.part3",
      words: [
        "Our",
        "AI",
        "generates",
        "quiz",
        "questions",
        "and",
        "flashcards",
        "from",
        "the",
        "video",
        "content,",
        "each",
        "with",
        "citation",
        "links",
        "back",
        "to",
        "the",
        "source.",
      ],
      wordsZh: [
        "我们的",
        "AI",
        "会",
        "从",
        "视频",
        "内容中",
        "生成",
        "测验题",
        "和",
        "闪卡，",
        "每个",
        "都",
        "带有",
        "引用",
        "链接",
        "指向",
        "原文。",
      ],
    },
    {
      timestamp: "01:00",
      seconds: 60,
      textKey: "modern.featureSections.youtube.mockContent.part4",
      words: [
        "Need",
        "to",
        "understand",
        "a",
        "video",
        "in",
        "another",
        "language?",
        "YouWo AI",
        "can",
        "translate",
        "the",
        "transcript",
        "to",
        "over",
        "50",
        "languages",
        "instantly.",
      ],
      wordsZh: [
        "需要",
        "理解",
        "另一种",
        "语言的",
        "视频？",
        "由我AI",
        "可以",
        "即时",
        "将",
        "转录文本",
        "翻译成",
        "50多种",
        "语言。",
      ],
    },
    {
      timestamp: "01:15",
      seconds: 75,
      textKey: "modern.featureSections.youtube.mockContent.part5",
      words: [
        "Use",
        "the",
        "search",
        "feature",
        "to",
        "find",
        "specific",
        "keywords",
        "across",
        "the",
        "entire",
        "transcript.",
        "Results",
        "link",
        "directly",
        "to",
        "timestamps.",
      ],
      wordsZh: [
        "使用",
        "搜索",
        "功能",
        "在",
        "整个",
        "转录文本中",
        "查找",
        "特定的",
        "关键词。",
        "结果",
        "直接",
        "链接到",
        "时间戳。",
      ],
    },
    {
      timestamp: "02:30",
      seconds: 150,
      textKey: "modern.featureSections.youtube.mockContent.part6",
      words: [
        "Each",
        "section",
        "gets",
        "its",
        "own",
        "summary",
        "highlighting",
        "key",
        "concepts.",
        "Click",
        "a",
        "summary",
        "to",
        "jump",
        "to",
        "that",
        "part",
        "of",
        "the",
        "video.",
      ],
      wordsZh: [
        "每个",
        "章节",
        "都有",
        "自己的",
        "摘要，",
        "突出",
        "关键",
        "概念。",
        "点击",
        "摘要",
        "跳转到",
        "视频的",
        "相应",
        "部分。",
      ],
    },
    {
      timestamp: "02:45",
      seconds: 165,
      textKey: "modern.featureSections.youtube.mockContent.part7",
      words: [
        "You",
        "can",
        "also",
        "create",
        "custom",
        "summaries",
        "focusing",
        "on",
        "specific",
        "topics",
        "or",
        "themes",
        "from",
        "the",
        "video",
        "content.",
      ],
      wordsZh: [
        "你",
        "还可以",
        "创建",
        "自定义",
        "摘要，",
        "专注于",
        "视频",
        "内容中",
        "特定的",
        "主题",
        "或",
        "话题。",
      ],
    },
  ];

  // Summary content with inline timestamp citations (like PDF page citations)
  const summaryContent = {
    overviewKey: "modern.featureSections.youtube.mockContent.summaryOverview",
    overviewCitations: ["00:00", "00:15"],
    keyPoints: [
      {
        textKey: "modern.featureSections.youtube.mockContent.summaryKeyPoint0",
        citation: "00:15",
        partIndex: 1,
      },
      {
        textKey: "modern.featureSections.youtube.mockContent.summaryKeyPoint1",
        citation: "00:45",
        partIndex: 3,
      },
      {
        textKey: "modern.featureSections.youtube.mockContent.summaryKeyPoint2",
        citation: "01:00",
        partIndex: 4,
      },
    ],
  };

  // Track click animation to prevent multiple triggers
  const [clickAnimationKey, setClickAnimationKey] = useState(0);

  // Timestamp citation badge component - with bulge preview before click
  const TimestampBadge = ({
    time,
    onClick,
    isClicked,
    isBulging,
    animKey,
    badgeRef,
  }: {
    time: string;
    onClick?: () => void;
    isClicked?: boolean;
    isBulging?: boolean;
    animKey?: number;
    badgeRef?: React.RefObject<HTMLSpanElement>;
  }) => {
    // Use a local ref to track if this specific click has been animated
    const hasAnimatedRef = useRef<number>(0);
    const shouldAnimate =
      isClicked && animKey !== undefined && hasAnimatedRef.current !== animKey;

    // Mark as animated when click starts
    useEffect(() => {
      if (isClicked && animKey !== undefined) {
        hasAnimatedRef.current = animKey;
      }
    }, [isClicked, animKey]);

    return (
      <motion.span
        ref={badgeRef}
        className={`inline-flex items-center text-[11px] px-2 py-0.5 rounded-full ml-1 font-mono cursor-pointer hover:bg-blue-100 active:scale-95`}
        style={{
          backgroundColor: isClicked ? Color.themeBlue : `${Color.themeBlue}15`,
          color: isClicked ? Color.white : Color.themeBlue,
        }}
        initial={{ scale: 1, boxShadow: "0 0 0 rgba(78, 164, 245, 0)" }}
        animate={
          isBulging
            ? { scale: 1.1, boxShadow: "0 0 20px rgba(78, 164, 245, 0.4)" }
            : shouldAnimate
              ? {
                  scale: [1, 1.15, 1],
                  boxShadow: "0 0 0 rgba(78, 164, 245, 0)",
                }
              : { scale: 1, boxShadow: "0 0 0 rgba(78, 164, 245, 0)" }
        }
        transition={{ duration: isBulging ? 0.5 : 0.3, ease: "easeOut" }}
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) onClick();
        }}
      >
        {time}
      </motion.span>
    );
  };

  // Ref for playing interval
  const playIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to get element position relative to card
  // Adjusts for cursor tip offset (the SVG cursor tip is at ~5px right, ~3px down from container origin)
  const CURSOR_TIP_OFFSET_X = 5;
  const CURSOR_TIP_OFFSET_Y = 3;

  const getElementPositionInCard = (
    elementRef: React.RefObject<HTMLElement>,
  ): { x: number; y: number } | null => {
    if (!elementRef.current || !cardRef.current) return null;
    const cardRect = cardRef.current.getBoundingClientRect();
    const elementRect = elementRef.current.getBoundingClientRect();
    // Return position so cursor TIP lands on element center
    return {
      x:
        elementRect.left -
        cardRect.left +
        elementRect.width / 2 -
        CURSOR_TIP_OFFSET_X,
      y:
        elementRect.top -
        cardRect.top +
        elementRect.height / 2 -
        CURSOR_TIP_OFFSET_Y,
    };
  };

  // Handle user interaction - pause auto animation, but allow manual play
  const handleUserInteraction = () => {
    setIsAnimationPaused(true);
    animationAbortRef.current = true;
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => {
      setIsAnimationPaused(false);
      animationAbortRef.current = false;
    }, 30000);
  };

  // Start playing from a specific part and word
  const startPlayingFrom = (partIndex: number, wordIndex: number = 0) => {
    // Clear any existing play interval
    if (playIntervalRef.current) {
      clearInterval(playIntervalRef.current);
    }

    setCurrentPartIndex(partIndex);
    setCurrentWordIndex(wordIndex);

    const part = transcriptContent[partIndex];
    const baseSeconds = part.seconds;
    const wordsInPart = (isZh ? part.wordsZh : part.words).length;

    // Play words one by one
    let currentWord = wordIndex;
    playIntervalRef.current = setInterval(() => {
      if (currentWord < wordsInPart) {
        setCurrentWordIndex(currentWord);
        // Calculate time progress within the section (15 seconds per section)
        const currentSec = baseSeconds + (currentWord / wordsInPart) * 15;
        setCurrentTime(formatTime(currentSec));
        setProgressPercent((currentSec / totalDuration) * 100);
        currentWord++;
      } else {
        // Finished this part, stop playing
        if (playIntervalRef.current) {
          clearInterval(playIntervalRef.current);
        }
        setCurrentWordIndex(-1);
      }
    }, 150);
  };

  // Scroll transcript to align a specific part at the top (only scroll the container, not the page)
  const scrollToPartAligned = (partIndex: number) => {
    if (transcriptScrollRef.current) {
      const container = transcriptScrollRef.current;
      const partElement = container.querySelector(
        `[data-part="${partIndex}"]`,
      ) as HTMLElement;
      if (partElement) {
        // Calculate scroll position relative to the container
        const containerRect = container.getBoundingClientRect();
        const elementRect = partElement.getBoundingClientRect();
        const scrollTop =
          container.scrollTop + (elementRect.top - containerRect.top);
        container.scrollTo({ top: scrollTop, behavior: "smooth" });
      }
    }
  };

  // Handle timestamp click - jump to that part, scroll, and start playing
  const handleTimestampClick = (
    partIndex: number,
    seconds: number,
    timestamp: string,
  ) => {
    handleUserInteraction();
    setClickedSection(partIndex);
    setTimeout(() => setClickedSection(null), 600);

    // Scroll transcript to align this section at top
    scrollToPartAligned(partIndex);

    // Start playing from this section
    startPlayingFrom(partIndex, 0);
  };

  // Handle word click - jump to that exact word and start playing
  const handleWordClick = (partIndex: number, wordIndex: number) => {
    handleUserInteraction();

    // Scroll transcript to align this section at top
    scrollToPartAligned(partIndex);

    // Start playing from this word
    startPlayingFrom(partIndex, wordIndex);
  };

  // Cleanup play interval on unmount
  useEffect(() => {
    return () => {
      if (playIntervalRef.current) {
        clearInterval(playIntervalRef.current);
      }
    };
  }, []);

  // Format seconds to MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Animation sequence - only runs when card is in view
  useEffect(() => {
    // Don't start animation if not in view
    if (!isInView) {
      animationStartedRef.current = false;
      return;
    }

    // Prevent double-run in React Strict Mode
    if (animationStartedRef.current) return;
    animationStartedRef.current = true;

    let isMounted = true;

    const wait = (ms: number) =>
      new Promise<boolean>((resolve) => {
        setTimeout(() => {
          if (animationAbortRef.current || !isMounted) resolve(false);
          else resolve(true);
        }, ms);
      });

    const runAnimation = async () => {
      if (isAnimationPaused || animationAbortRef.current || !isInView) return;

      // Average reading speed: ~250 words per minute = ~240ms per word
      const READING_SPEED_MS = 200; // ms per word

      // Reset card state
      setActiveTab("summary");
      setCurrentWordIndex(-1);
      setCurrentPartIndex(0);
      setCurrentTime("00:00");
      setProgressPercent(0);
      setClickedSection(null);
      setClickedCitation(null);
      setBulgingElement(null);
      setIsPaused(false);
      // Only hide bullet points if they haven't been shown yet
      if (!bulletPointsShownRef.current) {
        setShowBulletPoints(false);
      }
      if (transcriptScrollRef.current) {
        transcriptScrollRef.current.scrollTo({ top: 0 });
      }

      // Start animation immediately
      if (!(await wait(0))) return;

      // Target: ~8 seconds total animation
      // Each section plays for ~3-4 seconds max
      const MAX_PLAY_TIME_MS = 3000; // 3 seconds per section
      const WORD_SPEED_MS = 150; // Speed per word when playing

      const part0Words = isZh
        ? transcriptContent[0].wordsZh
        : transcriptContent[0].words;
      const maxWordsSection1 = Math.floor(MAX_PLAY_TIME_MS / WORD_SPEED_MS);

      // Get positions from refs
      const wordPos = getElementPositionInCard(firstWordRef);
      const citation45Pos = getElementPositionInCard(citation45Ref);
      if (!wordPos || !citation45Pos) return; // Refs not ready

      // ============ CLICK 1: Click on first word in transcript ============
      // Mouse appears at left side of card, then moves to first word
      setMousePosition({
        x: 20,
        y: 180,
        visible: true,
        clicking: false,
      });
      if (!(await wait(200))) return;

      // Mouse moves to the first word
      setMousePosition({
        x: wordPos.x,
        y: wordPos.y,
        visible: true,
        clicking: false,
      });
      if (!(await wait(400))) return;

      // Mouse clicks on the first word
      setMousePosition({
        x: wordPos.x,
        y: wordPos.y,
        visible: true,
        clicking: true,
      });
      if (!(await wait(150))) return;

      // Release click and start playing from beginning
      setMousePosition({
        x: wordPos.x,
        y: wordPos.y,
        visible: true,
        clicking: false,
      });
      setCurrentPartIndex(0);
      setCurrentWordIndex(0);
      setCurrentTime("00:00");
      setProgressPercent(0);
      if (!(await wait(200))) return;

      // Hide mouse while playing
      setMousePosition((prev) => ({ ...prev, visible: false }));

      // Play first section for ~3 seconds (limited words)
      const wordsToPlay1 = Math.min(maxWordsSection1, part0Words.length);
      for (let wordIdx = 0; wordIdx < wordsToPlay1; wordIdx++) {
        if (animationAbortRef.current || !isMounted || !isInView) return;
        setCurrentPartIndex(0);
        setCurrentWordIndex(wordIdx);
        const currentSec = (wordIdx / part0Words.length) * 15;
        setCurrentTime(formatTime(currentSec));
        setProgressPercent((currentSec / totalDuration) * 100);

        // At ~80%, start showing mouse moving towards citation
        if (wordIdx === Math.floor(wordsToPlay1 * 0.8)) {
          setMousePosition({
            x: wordPos.x + 100,
            y: wordPos.y - 80,
            visible: true,
            clicking: false,
          });
        }

        await new Promise((r) => setTimeout(r, WORD_SPEED_MS));
      }

      // ============ CLICK 2: Click on 00:45 citation ============
      // Mouse moves towards the 00:45 citation (in Key Points section)
      setMousePosition({
        x: citation45Pos.x,
        y: citation45Pos.y,
        visible: true,
        clicking: false,
      });
      if (!(await wait(400))) return;

      // Bulge preview on 00:45 citation before clicking
      setBulgingElement("citation-00:45");
      if (!(await wait(300))) return;

      // Mouse clicks the citation (single click)
      setMousePosition({
        x: citation45Pos.x,
        y: citation45Pos.y,
        visible: true,
        clicking: true,
      });
      setBulgingElement(null);
      setClickAnimationKey((prev) => prev + 1);
      setClickedCitation("00:45");
      if (!(await wait(150))) return;

      // Release click - jump to 00:45
      setMousePosition({
        x: citation45Pos.x,
        y: citation45Pos.y,
        visible: true,
        clicking: false,
      });
      setClickedCitation(null);
      setCurrentPartIndex(3);
      setCurrentWordIndex(-1);
      setCurrentTime("00:45");
      setProgressPercent((45 / totalDuration) * 100);
      scrollToPartAligned(3);
      if (!(await wait(100))) return;

      // Hide mouse while playing section
      setMousePosition((prev) => ({ ...prev, visible: false }));

      // Play section 00:45 for ~3 seconds
      const part3Words = isZh
        ? transcriptContent[3].wordsZh
        : transcriptContent[3].words;
      const wordsToPlay3 = Math.min(maxWordsSection1, part3Words.length);
      for (let wordIdx = 0; wordIdx < wordsToPlay3; wordIdx++) {
        if (animationAbortRef.current || !isMounted || !isInView) return;
        setCurrentPartIndex(3);
        setCurrentWordIndex(wordIdx);
        const currentSec = 45 + (wordIdx / part3Words.length) * 15;
        setCurrentTime(formatTime(currentSec));
        setProgressPercent((currentSec / totalDuration) * 100);
        await new Promise((r) => setTimeout(r, WORD_SPEED_MS));
      }

      // Animation complete - show pause and reveal bullet points
      setCurrentWordIndex(-1);
      setIsPaused(true);
      setShowBulletPoints(true);
      bulletPointsShownRef.current = true;

      // Wait for reading bullet points (~5 seconds)
      if (!(await wait(5000))) return;

      // Wait before restarting loop (8 seconds)
      if (!(await wait(8000))) return;
    };

    const animationLoop = async () => {
      while (isMounted) {
        if (!isAnimationPaused && !animationAbortRef.current) {
          await runAnimation();
        }
        await new Promise((r) => setTimeout(r, 500));
      }
    };

    animationLoop();

    return () => {
      isMounted = false;
      animationStartedRef.current = false;
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, [isAnimationPaused, isInView, isZh]);

  // Get word style - only highlight CURRENT word
  const getWordStyle = (partIndex: number, wordIndex: number) => {
    if (partIndex === currentPartIndex && wordIndex === currentWordIndex) {
      return {
        backgroundColor: Color.themeBlue,
        color: Color.white,
        padding: "1px 4px",
        borderRadius: "3px",
      };
    }
    return {};
  };

  return (
    <section
      className={`${showDescription ? "py-8 md:py-20" : "py-0"} bg-white overflow-hidden`}
    >
      <div className={showDescription ? "container mx-auto px-4" : ""}>
        <div
          className={`${showDescription ? "grid md:grid-cols-2 gap-12 lg:gap-20 items-center max-w-7xl mx-auto" : "flex justify-center"}`}
        >
          {/* Visual Mockup - Right on desktop */}
          <motion.div
            initial={{ opacity: 0, x: showDescription ? 50 : 0 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-2 px-4 md:px-0"
          >
            {/* Card wrapper with relative positioning for badge */}
            <div style={{ position: "relative", width: "fit-content" }}>
              {/* Video Interface Mockup */}
              <div
                ref={cardRef}
                className="rounded-3xl overflow-hidden shadow-2xl"
                style={{
                  backgroundColor: Color.white,
                  border: `1px solid ${Color.borderLight}`,
                }}
                onMouseDown={handleUserInteraction}
                onTouchStart={handleUserInteraction}
              >
                {/* Mouse Cursor Overlay */}
                <AnimatePresence>
                  {mousePosition.visible && (
                    <motion.div
                      className="pointer-events-none"
                      style={{
                        position: "absolute",
                        zIndex: 100,
                        left: mousePosition.x,
                        top: mousePosition.y,
                      }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{
                        opacity: 1,
                        scale: mousePosition.clicking ? 0.85 : 1,
                        left: mousePosition.x,
                        top: mousePosition.y,
                      }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 25,
                      }}
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86a.5.5 0 0 1 .35-.15h6.87c.45 0 .67-.54.35-.85L6.35 2.86a.5.5 0 0 0-.85.35Z"
                          fill="#fff"
                          stroke="#000"
                          strokeWidth="1.5"
                        />
                      </svg>
                      {mousePosition.clicking && (
                        <motion.div
                          className="absolute -top-1 -left-1 w-8 h-8 rounded-full"
                          style={{ backgroundColor: "rgba(78, 164, 245, 0.3)" }}
                          initial={{ scale: 0.5, opacity: 1 }}
                          animate={{ scale: 1.5, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                        />
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Main Content - Side by Side */}
                <div className="flex" style={{ minHeight: 440 }}>
                  {/* LEFT: Video Player on TOP + Transcript on BOTTOM */}
                  <div className="w-[55%] flex flex-col">
                    {/* Video Player Area with YouWo AI screenshot */}
                    <div
                      className="relative"
                      style={{ backgroundColor: "#f8f9fa", height: 160 }}
                    >
                      {/* YouWo AI app screenshot as thumbnail */}
                      <img
                        src="/landing/youtube-thumbnail.webp"
                        alt="YouWo AI Tutorial"
                        className="w-full h-full object-cover"
                        style={{ objectPosition: "top center" }}
                      />

                      {/* Progress bar */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-300">
                        <motion.div
                          className="h-full"
                          style={{ backgroundColor: Color.videoRed }}
                          animate={{ width: `${progressPercent}%` }}
                          transition={{ duration: 0.15, ease: "linear" }}
                        />
                      </div>

                      {/* Time display - grayed out as secondary */}
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/40 rounded text-white/50 text-xs font-mono">
                        {currentTime} / 08:45
                      </div>

                      {/* YouTube badge - grayed out */}
                      <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-black/30 rounded">
                        <Icon
                          icon="logos:youtube-icon"
                          className="text-sm opacity-50"
                        />
                      </div>

                      {/* Pause overlay - shows at end of animation */}
                      <AnimatePresence>
                        {isPaused && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute inset-0 flex items-center justify-center bg-black/30"
                          >
                            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-white/90">
                              <Icon
                                icon="lucide:pause"
                                className="text-xl text-gray-800"
                              />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Transcript - BOTTOM LEFT (Recording card style) - Fixed height scroll view */}
                    <div
                      ref={transcriptScrollRef}
                      className="overflow-y-auto p-3"
                      style={{
                        ...hideScrollbarStyle,
                        borderTop: `1px solid ${Color.borderLight}`,
                        height: 240,
                      }}
                    >
                      {transcriptContent.map((part, partIndex) => (
                        <div
                          key={partIndex}
                          data-part={partIndex}
                          className="mb-4"
                        >
                          {/* Timestamp - clickable to jump and play */}
                          <div
                            className="text-sm font-semibold mb-1 cursor-pointer hover:opacity-80 transition-opacity inline-block"
                            style={{ color: Color.textHintStrong }}
                            onClick={() =>
                              handleTimestampClick(
                                partIndex,
                                part.seconds,
                                part.timestamp,
                              )
                            }
                          >
                            {part.timestamp}
                          </div>
                          {/* Text - each word is clickable to jump and play from that word */}
                          <p
                            className="leading-relaxed"
                            style={{
                              fontSize: 15,
                              color: Color.textPrimary,
                            }}
                          >
                            {(isZh ? part.wordsZh : part.words).map(
                              (word, wordIndex) => (
                                <span key={wordIndex}>
                                  <span
                                    ref={
                                      partIndex === 0 && wordIndex === 0
                                        ? firstWordRef
                                        : undefined
                                    }
                                    className="transition-all duration-100 cursor-pointer hover:bg-gray-50/50 rounded"
                                    style={getWordStyle(partIndex, wordIndex)}
                                    onClick={() =>
                                      handleWordClick(partIndex, wordIndex)
                                    }
                                  >
                                    {word}
                                  </span>
                                  {!isZh &&
                                  wordIndex <
                                    (isZh ? part.wordsZh : part.words).length -
                                      1
                                    ? " "
                                    : ""}
                                </span>
                              ),
                            )}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* RIGHT: Summary Panel with tabs */}
                  <div
                    className="flex-1 flex flex-col"
                    style={{
                      backgroundColor: Color.white,
                      borderLeft: `1px solid ${Color.borderLight}`,
                    }}
                  >
                    {/* Tabs - PDF style pill buttons */}
                    <div
                      className="flex items-center px-2 py-2 gap-1 overflow-x-auto flex-shrink-0"
                      style={hideScrollbarStyle}
                    >
                      {tabs.map((tab) => (
                        <button
                          key={tab.id}
                          className={`px-3 py-1.5 text-[10px] font-medium rounded-full whitespace-nowrap transition-colors ${
                            activeTab === tab.id
                              ? "text-white"
                              : "text-gray-600 hover:bg-gray-50"
                          }`}
                          style={{
                            backgroundColor:
                              activeTab === tab.id
                                ? Color.themeBlue
                                : "transparent",
                            border:
                              activeTab === tab.id
                                ? "none"
                                : `1px solid ${Color.borderLight}`,
                          }}
                          onClick={() => {
                            handleUserInteraction();
                            setActiveTab(tab.id);
                          }}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>

                    {/* Tab Content - Summary with inline timestamp citations */}
                    <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                      <AnimatePresence mode="wait">
                        {activeTab === "summary" ? (
                          <motion.div
                            key="summary"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="p-4 space-y-4 overflow-y-auto flex-1"
                            style={hideScrollbarStyle}
                          >
                            {/* Overview */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                {t("landing.features.youtube.card.overview")}
                              </h4>
                              <p className="text-[13px] text-gray-600 leading-relaxed">
                                {t(summaryContent.overviewKey)}
                                {summaryContent.overviewCitations.map(
                                  (timestamp, idx) => {
                                    const part = transcriptContent.find(
                                      (p) => p.timestamp === timestamp,
                                    );
                                    const partIndex = part
                                      ? transcriptContent.indexOf(part)
                                      : 0;
                                    return (
                                      <TimestampBadge
                                        key={idx}
                                        time={timestamp}
                                        isClicked={
                                          clickedCitation === timestamp
                                        }
                                        isBulging={
                                          bulgingElement ===
                                          `citation-${timestamp}`
                                        }
                                        animKey={clickAnimationKey}
                                        onClick={() =>
                                          handleTimestampClick(
                                            partIndex,
                                            part?.seconds || 0,
                                            timestamp,
                                          )
                                        }
                                      />
                                    );
                                  },
                                )}
                              </p>
                            </div>

                            {/* Key Points */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                {t("landing.features.youtube.card.keyPoints")}
                              </h4>
                              <div className="space-y-2">
                                {summaryContent.keyPoints.map((point, idx) => (
                                  <p
                                    key={idx}
                                    className="text-[13px] text-gray-600 leading-relaxed"
                                  >
                                    • {t(point.textKey)}
                                    <TimestampBadge
                                      time={point.citation}
                                      isClicked={
                                        clickedCitation === point.citation
                                      }
                                      isBulging={
                                        bulgingElement ===
                                        `citation-${point.citation}`
                                      }
                                      animKey={clickAnimationKey}
                                      badgeRef={
                                        point.citation === "00:45"
                                          ? citation45Ref
                                          : undefined
                                      }
                                      onClick={() =>
                                        handleTimestampClick(
                                          point.partIndex,
                                          transcriptContent[point.partIndex]
                                            .seconds,
                                          point.citation,
                                        )
                                      }
                                    />
                                  </p>
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        ) : (
                          <motion.div
                            key="ai-chat"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex-1 flex flex-col items-center justify-center text-center px-4"
                          >
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
                              style={{ backgroundColor: "#f5f5f5" }}
                            >
                              <Icon
                                icon="lucide:sparkles"
                                className="text-lg text-gray-300"
                              />
                            </div>
                            <p className="text-[10px] text-gray-300">
                              {t("landing.features.youtube.card.askAboutVideo")}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating badge - top right, inside wrapper */}
              <motion.div
                className="px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5"
                style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  backgroundColor: Color.videoRed,
                  zIndex: 10,
                }}
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Icon
                  icon="lucide:mouse-pointer-click"
                  className="text-white text-sm"
                />
                <span className="text-white text-xs font-medium">
                  {t("landing.features.youtube.card.clickToJump")}
                </span>
              </motion.div>
            </div>
          </motion.div>

          {/* Text Content - Left */}
          {showDescription && (
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1"
            >
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                style={{ backgroundColor: `${Color.videoRed}15` }}
              >
                <Icon icon="lucide:video" style={{ color: Color.videoRed }} />
                <span
                  style={{ color: Color.videoRed }}
                  className="font-medium text-sm"
                >
                  {t("landing.featureDetail.youtube.title")}
                </span>
              </div>

              <h2 className="text-11xl md:text-17xl lg:text-29xl font-bold text-gray-900 mb-6 leading-tight mt-0">
                {t("landing.features.youtube.headline")}
              </h2>

              <p
                className="text-lg md:text-xl mb-8 leading-relaxed"
                style={{ color: "#726F6F" }}
              >
                {t("landing.featureDetail.youtube.description")}
              </p>

              {/* Highlights - always visible on mobile, animated on desktop */}
              {/* Mobile version - always visible */}
              <div className="block md:hidden space-y-4">
                {[
                  {
                    icon: "lucide:list",
                    text: t("landing.featureDetail.youtube.highlight1"),
                  },
                  {
                    icon: "lucide:mouse-pointer-click",
                    text: t("landing.featureDetail.youtube.highlight2"),
                  },
                  {
                    icon: "lucide:highlighter",
                    text:
                      t("landing.featureDetail.youtube.highlight4") ||
                      "Highlight any text to ask questions instantly",
                  },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${Color.videoRed}15` }}
                    >
                      <Icon
                        icon={item.icon}
                        className="text-lg"
                        style={{ color: Color.videoRed }}
                      />
                    </div>
                    <span className="text-base text-gray-700">{item.text}</span>
                  </div>
                ))}
              </div>
              {/* Desktop version - animated after card animation completes */}
              <div className="hidden md:block" style={{ minHeight: 140 }}>
                <motion.div
                  className="space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: showBulletPoints ? 1 : 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {[
                    {
                      icon: "lucide:list",
                      text: t("landing.featureDetail.youtube.highlight1"),
                    },
                    {
                      icon: "lucide:mouse-pointer-click",
                      text: t("landing.featureDetail.youtube.highlight2"),
                    },
                    {
                      icon: "lucide:highlighter",
                      text:
                        t("landing.featureDetail.youtube.highlight4") ||
                        "Highlight any text to ask questions instantly",
                    },
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center gap-3"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{
                        opacity: showBulletPoints ? 1 : 0,
                        x: showBulletPoints ? 0 : -20,
                      }}
                      transition={{
                        delay: showBulletPoints ? index * 0.15 : 0,
                        duration: 0.4,
                      }}
                    >
                      <div
                        className="w-9 h-9 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${Color.videoRed}15` }}
                      >
                        <Icon
                          icon={item.icon}
                          className="text-lg"
                          style={{ color: Color.videoRed }}
                        />
                      </div>
                      <span className="text-base text-gray-700">
                        {item.text}
                      </span>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeatureSectionYouTube;
